import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  OnInit,
  viewChild,
  OnDestroy,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { JobTitleService } from '../job-title.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DualLangInputComponent } from '@areeb-innovative-technologies/angular_multi_languages_input_lib';
import { Subscription } from 'rxjs';
import { ScrollToErrorDirective } from '../../../../../common/directives/scroll-to-error/scroll-to-error.directive';
import { OverlayLoaderComponent } from '../../../../../common/components/overlay-loader/overlay-loader.component';
import { TenantPathPipe } from '../../../../../common/pipes/tenant-path/tenant-path.pipe';
import { ValidationMessageComponent } from '../../../../../../ui-components/lib/validation-message/validation-message.component';
import { InputComponent } from '../../../../../../ui-components/lib/input/input.component';
import { PaginatedSelectComponent } from '../../../../../../ui-components/lib/paginated-select/paginated-select.component';
import { ADMIN_ROUTES } from '../../../../../common/constants/admin.routing.constants';
import { TenantService } from '../../../../../common/services/tenant/tenant.service';
import { OptionItem } from '../../../../../../ui-components/lib/option.interface';
import {
  LookupFilter,
  LookupOptionItem,
} from '../../../../../../ui-components/lib/lookup-field/lookup-field-item';
import { ENDPOINTS } from '../../../../../../core/common/constants/endpoints.constants';
import { filterInputTypesEnum } from '../../../../../../ui-components/enums/filter-input-type';
import { FormService } from '../../../../../../form-builder/services/form/form.service';
import { ToasterService } from '../../../../../../ui-components/services/toast/toast.service';
import { FormValidation } from '../../../../../../ui-components/enums/form-validation';
import { dualLanguageCustomValidators } from '../../../../../../form-builder/services/form/custom-validators';

@Component({
  selector: 'lib-job-title-form',
  imports: [
    InputComponent,
    ValidationMessageComponent,
    TranslocoPipe,
    ReactiveFormsModule,
    OverlayLoaderComponent,
    DualLangInputComponent,
    RouterLink,
    ScrollToErrorDirective,
    TenantPathPipe,
    PaginatedSelectComponent,
  ],
  templateUrl: './job-title-form.component.html',
  styleUrl: './job-title-form.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class JobTitleFormComponent implements OnInit, OnDestroy {
  readonly ADMIN_ROUTES = ADMIN_ROUTES;

  route = inject(ActivatedRoute);
  toaster = inject(ToasterService);
  formService = inject(FormService);
  jobTitleService = inject(JobTitleService);
  router = inject(Router);
  tenantService = inject(TenantService);

  addJobTitleForm = signal<FormGroup>(new FormGroup({}));
  isLoading = signal<boolean>(false);
  isPageLoading = signal<boolean>(false);
  editMode = signal<boolean>(false);
  isFormChanged = signal<boolean>(false);
  jobTitleId = '';
  initialFormValues: any = {};
  scrollToError = viewChild.required(ScrollToErrorDirective);
  subscriptions = new Subscription();

  trackOptions = signal<OptionItem[]>([]);
  radfordGradeOptions = signal<OptionItem[]>([]);
  jobFamilyOptions = signal<OptionItem[]>([]);

  readonly variableOptionsApi = ENDPOINTS.Lookup.VariableOptionsSorted;
  private readonly baseVariableFilters = (key: string): LookupFilter[] => [
    {
      key: 'variable.module',
      value: 'user_management',
      value_type: filterInputTypesEnum.STATIC,
    },
    {
      key: 'variable.key',
      value: key,
      value_type: filterInputTypesEnum.STATIC,
    },
    {
      key: 'is_active',
      value: '1',
      value_type: filterInputTypesEnum.STATIC,
    },
  ];
  readonly trackFilter = this.baseVariableFilters('career_track');
  readonly radfordGradeFilter = this.baseVariableFilters('radford_grade');
  readonly jobFamilyFilter = this.baseVariableFilters('job_family');
  selectedTrack!: LookupOptionItem;
  selectedRadfordGrade!: LookupOptionItem;
  selectedJobFamily!: LookupOptionItem;

  ngOnInit() {
    this.initForm();
    this.subscriptions.add(
      this.route.params.subscribe((params: any) => {
        this.jobTitleId = params.id;
        this.jobTitleId ? this.editMode.set(true) : this.editMode.set(false);
        if (this.editMode()) this.getJobTitleDetails();
      })
    );
  }

  getJobTitleDetails() {
    this.isPageLoading.set(true);
    this.subscriptions.add(
      this.jobTitleService.getJobTitleDetails(this.jobTitleId).subscribe({
        next: res => {
          const patchValue: any = {
            name: {
              en: res.data.translations.name.en,
              ar: res.data.translations.name.ar,
            },
            identifier: res.data.identifier ?? '',
            track_option_id: res.data.track_option_id ?? null,
            radford_grade_option_id: res.data.radford_grade_option_id ?? null,
            job_family_option_id: res.data.job_family_option_id ?? null,
          };

          if (res.data.track) {
            this.selectedTrack = {
              value: Number(res.data.track_option_id),
              label: res.data.track.label,
            };
          }
          if (res.data.radford_grade) {
            this.selectedRadfordGrade = {
              value: Number(res.data.radford_grade_option_id),
              label: res.data.radford_grade.label,
            };
          }
          if (res.data.job_family) {
            this.selectedJobFamily = {
              value: Number(res.data.job_family_option_id),
              label: res.data.job_family.label,
            };
          }
          this.addJobTitleForm().patchValue(patchValue);
          this.initialFormValues = this.addJobTitleForm().getRawValue();
        },
        error: () => {
          this.isPageLoading.set(false);
        },
        complete: () => {
          this.changeDetection();
          this.isPageLoading.set(false);
        },
      })
    );
  }

  prepareFormData() {
    const formValue = this.addJobTitleForm().getRawValue();
    const data: any = {
      name: {
        en: formValue.name.en,
        ar: formValue.name.ar,
      },
      identifier: formValue.identifier ?? '',
      track_option_id: formValue.track_option_id ?? null,
      radford_grade_option_id: formValue.radford_grade_option_id ?? null,
      job_family_option_id: formValue.job_family_option_id ?? null,
    };

    if (this.editMode()) {
      return { ...data, _method: 'put' };
    }

    return data;
  }

  returnUrl(formData: any, id: string) {
    return this.editMode()
      ? this.jobTitleService.editJobTitle(formData, id)
      : this.jobTitleService.addJobTitle(formData);
  }

  addEditJobTitle() {
    this.formService.syncDualLangControls(this.addJobTitleForm());

    if (this.addJobTitleForm().invalid) {
      this.addJobTitleForm().markAllAsTouched();
      this.scrollToError().scrollToFirstError(true);
    } else {
      this.isLoading.set(true);
      this.subscriptions.add(
        this.returnUrl(
          this.prepareFormData(),
          this.jobTitleId ? this.jobTitleId : ''
        ).subscribe({
          error: error => this.handleError(error),
          complete: () => {
            this.isLoading.set(false);
            if (this.editMode()) {
              this.router.navigate([
                this.tenantService.adminPath(
                  `${ADMIN_ROUTES.JOB_TITLES.DETAILS}`
                ),
                this.jobTitleId,
              ]);
            } else {
              this.router.navigate([
                this.tenantService.adminPath(`${ADMIN_ROUTES.JOB_TITLES.LIST}`),
              ]);
            }
          },
        })
      );
    }
  }

  checkForChanges() {
    const currentValues = this.addJobTitleForm().getRawValue();
    const hasChanges =
      JSON.stringify(currentValues) !== JSON.stringify(this.initialFormValues);
    this.isFormChanged.set(hasChanges);
  }

  changeDetection() {
    this.subscriptions.add(
      this.addJobTitleForm().valueChanges.subscribe(() => {
        this.checkForChanges();
      })
    );
  }

  initForm() {
    const baseControls: any = {
      name: new FormGroup(
        {
          en: new FormControl('', {
            validators: [
              Validators.minLength(2),
              Validators.maxLength(FormValidation.maxLength),
            ],
          }),
          ar: new FormControl('', {
            validators: [
              Validators.minLength(2),
              Validators.maxLength(FormValidation.maxLength),
            ],
          }),
        },
        {
          validators: [dualLanguageCustomValidators.atLeastOneLangRequired],
        }
      ),
      identifier: new FormControl('', [
        Validators.minLength(5),
        Validators.maxLength(10),
      ]),
    };

    baseControls.track_option_id = new FormControl(null);
    baseControls.radford_grade_option_id = new FormControl(null);
    baseControls.job_family_option_id = new FormControl(null);

    this.addJobTitleForm.set(new FormGroup(baseControls));
  }

  handleError(error: any) {
    this.isLoading.set(false);
    this.toaster.add(
      error.message ? error.message : error.error?.message,
      '',
      '',
      'error',
      true,
      true
    );
    this.formService.addParentFormGroupErrors(error, this.addJobTitleForm());
    this.scrollToError().scrollToFirstError(false);
  }

  goBack() {
    history.back();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
